// src/components/Popups/InstructorStudentsPopup.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import UIToggle from "../Common/UIToggle";
import UserAvatar from "../Common/UserAvatar";

import {
   getMyGroupStudents,
   getStudentPracticeProgress,
} from "../../api/groupsService";
import { getQuestionCategoriesWithCount } from "../../api/questionCategoriesService";

import { updateStudent } from "../../store/studentsSlice";
import {
   buildStudentExtrasPayload,
   clampInt,
   mergeStudentRecord,
   normStudentExtras,
   studentExtrasEqual,
   unwrapUserResponse,
} from "../../utils/studentExtrasUtils";

/* ================== small helpers ================== */
const readEnv = (viteKey, craKey) =>
   (typeof import.meta !== "undefined" &&
      import.meta?.env &&
      import.meta.env[viteKey]) ||
   (typeof process !== "undefined" && process?.env && process.env[craKey]) ||
   "";

const rawStartId = Number(
   readEnv("VITE_TICKETS_START", "REACT_APP_TICKETS_START"),
);
const rawCount = Number(
   readEnv("VITE_TICKETS_COUNT", "REACT_APP_TICKETS_COUNT"),
);
const START_ID =
   Number.isFinite(rawStartId) && rawStartId > 0 ? rawStartId : 246;
const COUNT =
   Number.isFinite(rawCount) && rawCount > 0 ? rawCount : 269 - 246 + 1;
const TICKET_IDS = Array.from({ length: COUNT }, (_, i) => START_ID + i);

/* ================== STATUS helpers ================== */
const STATUS = {
   PASSED: "PASSED",
   COMPLETED: "COMPLETED",
   FAILED: "FAILED",
   IN_PROGRESS: "IN_PROGRESS",
   NOT_STARTED: "NOT_STARTED",
};

function normalizeStatus(s) {
   const v = String(s || "").toUpperCase();
   if (v.includes("PASS")) return STATUS.PASSED;
   if (v.includes("COMP")) return STATUS.COMPLETED;
   if (v.includes("FAIL")) return STATUS.FAILED;
   if (v.includes("PROGRESS") || v.includes("STARTED"))
      return STATUS.IN_PROGRESS;
   if (v.includes("NOT")) return STATUS.NOT_STARTED;
   return STATUS.NOT_STARTED;
}

function statusToState(st) {
   const s = normalizeStatus(st);
   if (s === STATUS.PASSED || s === STATUS.COMPLETED) return "ok";
   if (s === STATUS.FAILED) return "bad";
   if (s === STATUS.IN_PROGRESS) return "progress";
   return "none";
}

function statusLabelRo(st) {
   const s = normalizeStatus(st);
   if (s === STATUS.PASSED || s === STATUS.COMPLETED) return "Trecut";
   if (s === STATUS.FAILED) return "Picat";
   if (s === STATUS.IN_PROGRESS) return "În lucru";
   return "Neînceput";
}

const firstLetter = (v) =>
   String(v || "")
      .trim()
      .charAt(0) || "";

function getInitials(student) {
   const fn = String(student?.firstName || "").trim();
   const ln = String(student?.lastName || "").trim();
   const a = firstLetter(fn);
   const b = firstLetter(ln);
   if (a && b) return (a + b).toUpperCase();
   if (fn) return fn.slice(0, 2).toUpperCase();
   return "–";
}

function hashStringToUInt(str) {
   let h = 0;
   for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
   return h >>> 0;
}

const AVATAR_COLORS = [
   "hsl(70 75% 88%)",
   "hsl(0 100% 88%)",
   "hsl(30 100% 86%)",
   "hsl(54 95% 86%)",
   "hsl(130 65% 84%)",
   "hsl(210 90% 84%)",
   "hsl(255 98% 84%)",
   "hsl(285 100% 84%)",
   "hsl(330 96% 84%)",
];

function getAvatarColorFromStudent(student) {
   const base =
      String(
         student?.id ??
            student?.email ??
            student?.phone ??
            `${student?.firstName || ""} ${student?.lastName || ""}`,
      ).trim() || "student";
   return AVATAR_COLORS[hashStringToUInt(base) % AVATAR_COLORS.length];
}

function resolveStudentIdnp(student) {
   return String(
      student?.idnp ??
         student?.IDNP ??
         student?.profile?.idnp ??
         student?.extras?.idnp ??
         "",
   ).trim();
}

function StudentProfileItem({ student }) {
   const fullName =
      `${student?.firstName || ""} ${student?.lastName || ""}`.trim() ||
      "Student";
   const phoneText = String(student?.phone || "—").trim() || "—";
   const idnp = resolveStudentIdnp(student);
   const meta = idnp ? `${phoneText} · IDNP ${idnp}` : phoneText;
   const initials = getInitials(student);
   const avatarBg = getAvatarColorFromStudent(student);

   return (
      <div className="instrStudentsPopup__studentCard">
         <UserAvatar
            person={student}
            color={avatarBg}
            initials={initials}
            className="instrStudentsPopup__studentAvatar"
            textClassName="instrStudentsPopup__studentAvatarText"
         />
         <div className="instrStudentsPopup__studentInfo">
            <h3 className="instrStudentsPopup__studentName">{fullName}</h3>
            <p className="instrStudentsPopup__studentMeta">{meta}</p>
         </div>
      </div>
   );
}

/* ================== UI helpers ================== */
function SegmentedBar({ pctCorrect, pctWrong, pctUnanswered, basePx = 22 }) {
   const ok = Math.max(0, Math.min(1, (pctCorrect ?? 0) / 100));
   const bad = Math.max(0, Math.min(1, (pctWrong ?? 0) / 100));
   const skip = Math.max(0, Math.min(1, (pctUnanswered ?? 0) / 100));

   return (
      <div className="instrStudentsPopup__bar" role="img">
         <div
            className="instrStudentsPopup__barInner"
            style={{
               "--base": `${basePx}px`,
               "--basesum": `calc(3 * ${basePx}px)`,
               "--ok": ok,
               "--bad": bad,
               "--skip": skip,
            }}
         >
            <div className="instrStudentsPopup__barSeg instrStudentsPopup__barSeg--ok" />
            <div className="instrStudentsPopup__barSeg instrStudentsPopup__barSeg--bad" />
            <div className="instrStudentsPopup__barSeg instrStudentsPopup__barSeg--skip" />
         </div>
      </div>
   );
}

/* ================== progress helpers ================== */
function normalizePagedResponse(raw) {
   if (Array.isArray(raw)) return raw;
   const items =
      raw?.data ||
      raw?.items ||
      raw?.results ||
      raw?.rows ||
      raw?.tickets ||
      raw?.categories ||
      [];
   if (Array.isArray(items)) return items;

   if (raw && typeof raw === "object") {
      const vals = Object.values(raw);
      if (
         Array.isArray(vals) &&
         vals.length > 0 &&
         vals.every((v) => v && typeof v === "object" && !Array.isArray(v))
      ) {
         return vals;
      }
   }

   return [];
}

function tsOf(it) {
   return (
      Date.parse(
         it?.completedAt ||
            it?.finishedAt ||
            it?.endedAt ||
            it?.startedAt ||
            it?.createdAt ||
            0,
      ) || 0
   );
}

function parseTicketNr(ticketName) {
   const s = String(ticketName || "");
   let m = s.match(/Practice\s*P\s*([0-9]+)/i);
   if (m) {
      const n = Number(m[1]);
      return Number.isInteger(n) && n > 0 ? n : null;
   }
   m = s.match(/(^|[^0-9])P\s*([0-9]{1,3})([^0-9]|$)/i);
   if (m) {
      const n = Number(m[2]);
      return Number.isInteger(n) && n > 0 ? n : null;
   }
   return null;
}

// Format real: "Category_Practice_15_1771851203077" → categoryId=15
function parseCategoryId(ticketName) {
   const s = String(ticketName || "");
   let m = s.match(/Category[_\s-]*Practice[_\s-]*([0-9]+)/i);
   if (m) {
      const n = Number(m[1]);
      return Number.isInteger(n) && n > 0 ? n : null;
   }
   m = s.replace(/[\s_-]+/g, "").match(/CategoryPractice([0-9]+)/i);
   if (m) {
      const n = Number(m[1]);
      return Number.isInteger(n) && n > 0 ? n : null;
   }
   return null;
}

function safeInt(v) {
   if (v == null) return null;
   if (typeof v === "string" && !v.trim()) return null;
   const n = Number(v);
   return Number.isFinite(n) ? n : null;
}

function getCatCount(category) {
   return (
      category?._count?.questions ??
      category?.questionCount ??
      category?.questionsCount ??
      category?.count ??
      category?.totalQuestions ??
      0
   );
}

function catTitleRo(category) {
   const ro = String(category?.nameRo ?? "").trim();
   const ru = String(category?.nameRu ?? "").trim();
   const name = String(
      category?.name ?? category?.title ?? category?.label ?? "",
   ).trim();
   return ro || name || ru || `#${category?.id}`;
}

function normalizeDisplayText(value) {
   return String(value || "")
      .replace(/[\r\n]+/g, " ")
      .replace(/\s+/g, " ")
      .replace(/^["'“”„]+|["'“”„]+$/g, "")
      .trim();
}

function toSentenceCase(value, lang = "ro") {
   const clean = normalizeDisplayText(value);
   if (!clean) return "";

   const locale = lang === "ru" ? "ru-RU" : "ro-RO";
   let normalized = clean.toLocaleLowerCase(locale);

   normalized = normalized.replace(
      /(^|[\s(])([a-zа-яăâîșț])\./giu,
      (_, prefix, letter) => `${prefix}${letter.toLocaleUpperCase(locale)}.`,
   );

   normalized = normalized.replace(
      /(^|[.!?]\s+|:\s+|\(\s*)([a-zа-яăâîșț])/giu,
      (_, prefix, letter) => `${prefix}${letter.toLocaleUpperCase(locale)}`,
   );

   return normalized;
}

function buildCategoryTitleParts(title, lang = "ro") {
   const clean = normalizeDisplayText(title);
   if (!clean) return [];

   const markerMatch = clean.match(
      /^\s*((?:\d+\.\d+(?:\.\d+)*\.?|\d+\.))\s*(.*)$/u,
   );

   if (!markerMatch) {
      return [{ marker: null, text: toSentenceCase(clean, lang) }];
   }

   const rawMarker = String(markerMatch[1] || "")
      .replace(/\s+/g, "")
      .trim();
   const marker = rawMarker
      ? rawMarker.endsWith(".")
         ? rawMarker
         : `${rawMarker}.`
      : null;
   const text = toSentenceCase(markerMatch[2], lang);

   return [{ marker, text: text || null }].filter(
      (part) => part.marker || part.text,
   );
}

function extractTicketNrFromItem(
   item,
   ticketIdToNrMap,
   displayBase = START_ID - 1,
) {
   const fromName = parseTicketNr(item?.ticketName);
   if (fromName) return fromName;

   const ticketId = safeInt(item?.ticketId ?? item?.ticket?.id);
   if (ticketId == null || ticketId <= 0) return null;

   const mapped = ticketIdToNrMap?.get?.(ticketId);
   if (mapped) return mapped;

   const nrFromId = ticketId - displayBase;
   return nrFromId > 0 ? nrFromId : null;
}

function extractCategoryIdFromItem(item) {
   const fromName = parseCategoryId(item?.ticketName);
   if (fromName) return fromName;

   const direct = safeInt(
      item?.categoryId ??
         item?.category?.id ??
         item?.questionCategoryId ??
         item?.questionCategory?.id ??
         item?.categoryID,
   );
   if (direct != null && direct > 0) return direct;
   return null;
}

function buildTicketAgg(practiceHistory, ticketIdToNrMap, displayBase) {
   const list = Array.isArray(practiceHistory) ? practiceHistory : [];
   const byNr = new Map();

   for (const it of list) {
      const nr = extractTicketNrFromItem(it, ticketIdToNrMap, displayBase);
      if (!nr) continue;
      const st = normalizeStatus(it?.status);
      const ts = tsOf(it);
      const prev = byNr.get(nr) || {
         latestAny: null,
         latestCompleted: null,
         latestInProgress: null,
      };
      if (!prev.latestAny || ts > prev.latestAny.ts)
         prev.latestAny = { ts, it };
      if (st === STATUS.IN_PROGRESS) {
         if (!prev.latestInProgress || ts > prev.latestInProgress.ts)
            prev.latestInProgress = { ts, it };
      } else {
         if (!prev.latestCompleted || ts > prev.latestCompleted.ts)
            prev.latestCompleted = { ts, it };
      }
      byNr.set(nr, prev);
   }

   const out = {};
   for (const [nr, rec] of byNr.entries()) {
      const base =
         rec.latestCompleted?.it ||
         rec.latestInProgress?.it ||
         rec.latestAny?.it ||
         null;
      const st = normalizeStatus(base?.status);
      const state = statusToState(st);
      const correct = safeInt(base?.score);
      const total = safeInt(base?.totalQuestions);
      const hasNewerInProgress =
         rec.latestInProgress &&
         (!rec.latestCompleted ||
            rec.latestInProgress.ts > rec.latestCompleted.ts);
      out[String(nr)] = {
         state,
         st,
         label: hasNewerInProgress ? "În lucru" : statusLabelRo(st),
         correct,
         total,
      };
   }
   return out;
}

function buildCategoryAgg(practiceHistory) {
   const list = Array.isArray(practiceHistory) ? practiceHistory : [];
   const byId = new Map();

   for (const it of list) {
      const cid = extractCategoryIdFromItem(it);
      if (!cid) continue;
      const st = normalizeStatus(it?.status);
      const ts = tsOf(it);
      const prev = byId.get(cid) || {
         latestAny: null,
         latestCompleted: null,
         latestInProgress: null,
         maxTotalQuestions: 0,
      };
      if (!prev.latestAny || ts > prev.latestAny.ts)
         prev.latestAny = { ts, it };
      if (st === STATUS.IN_PROGRESS) {
         if (!prev.latestInProgress || ts > prev.latestInProgress.ts)
            prev.latestInProgress = { ts, it };
      } else {
         if (!prev.latestCompleted || ts > prev.latestCompleted.ts)
            prev.latestCompleted = { ts, it };
      }
      const tq = Number(it?.totalQuestions || 0);
      if (tq > prev.maxTotalQuestions) prev.maxTotalQuestions = tq;
      byId.set(cid, prev);
   }

   const agg = {};
   for (const [cid, rec] of byId.entries()) {
      const base =
         rec.latestCompleted?.it ||
         rec.latestInProgress?.it ||
         rec.latestAny?.it ||
         null;
      const st = normalizeStatus(base?.status);
      const state = statusToState(st);
      const correct = safeInt(base?.score);
      const total = rec.maxTotalQuestions || safeInt(base?.totalQuestions) || 0;
      const hasNewerInProgress =
         rec.latestInProgress &&
         (!rec.latestCompleted ||
            rec.latestInProgress.ts > rec.latestCompleted.ts);

      agg[String(cid)] = {
         state,
         st,
         label: hasNewerInProgress ? "În lucru" : statusLabelRo(st),
         correct,
         total,
      };
   }
   return agg;
}

function getPracticeEntryKey(item, ticketIdToNrMap, displayBase) {
   const categoryId = extractCategoryIdFromItem(item);
   if (categoryId) return `category:${categoryId}`;

   const ticketNr = extractTicketNrFromItem(item, ticketIdToNrMap, displayBase);
   if (ticketNr) return `ticket:${ticketNr}`;

   const ticketId = safeInt(item?.ticketId ?? item?.ticket?.id);
   if (ticketId != null && ticketId > 0) return `ticket-id:${ticketId}`;

   const ticketName = String(item?.ticketName || "")
      .trim()
      .toLowerCase();
   if (ticketName) return `ticket-name:${ticketName}`;

   const rowId = safeInt(item?.id);
   if (rowId != null && rowId > 0) return `row:${rowId}`;

   return null;
}

function buildLatestPracticeEntries(
   practiceHistory,
   ticketIdToNrMap,
   displayBase,
) {
   const list = Array.isArray(practiceHistory) ? practiceHistory : [];
   const byKey = new Map();

   for (const it of list) {
      const key = getPracticeEntryKey(it, ticketIdToNrMap, displayBase);
      if (!key) continue;
      const ts = tsOf(it);
      const prev = byKey.get(key);
      if (!prev || ts > prev.ts) byKey.set(key, { ts, it });
   }

   return Array.from(byKey.values()).map((x) => x.it);
}

function formatHistoryDate(value) {
   const ts = Date.parse(value || "");
   if (!Number.isFinite(ts) || ts <= 0) return "—";
   try {
      return new Date(ts).toLocaleString("ro-RO", {
         day: "2-digit",
         month: "2-digit",
         year: "numeric",
         hour: "2-digit",
         minute: "2-digit",
      });
   } catch {
      return "—";
   }
}

/* ================== Main popup ================== */
export default function InstructorStudentsPopup({ initialStudent = null }) {
   const dispatch = useDispatch();

   const studentsState = useSelector((state) => state.students || {});
   const allUsers = Array.isArray(studentsState.list) ? studentsState.list : [];

   const studentId = initialStudent?.id ?? null;

   const studentFromRedux = useMemo(() => {
      if (!studentId) return null;
      return allUsers.find((u) => String(u?.id) === String(studentId)) || null;
   }, [allUsers, studentId]);

   const [loading, setLoading] = useState(true);
   const [err, setErr] = useState("");
   const [allowed, setAllowed] = useState(false);

   const [studentFromGroup, setStudentFromGroup] = useState(
      initialStudent || null,
   );
   const student = useMemo(
      () =>
         mergeStudentRecord(
            studentFromRedux,
            initialStudent,
            studentFromGroup,
         ),
      [studentFromRedux, studentFromGroup, initialStudent],
   );

   const [statistics, setStatistics] = useState(null);
   const [practiceHistory, setPracticeHistory] = useState([]);
   const [questionCategories, setQuestionCategories] = useState([]);
   const [categoriesLoading, setCategoriesLoading] = useState(false);

   const [tab, setTab] = useState("profile");
   const [q] = useState("");

   // extras
   const [extrasSaving, setExtrasSaving] = useState(false);
   const [extrasError, setExtrasError] = useState("");
   const [extrasBase, setExtrasBase] = useState(() => normStudentExtras(null));
   const [extrasForm, setExtrasForm] = useState(() => normStudentExtras(null));

   const [absText, setAbsText] = useState("0");
   const absFocusedRef = useRef(false);

   useEffect(() => {
      if (absFocusedRef.current) return;
      const n = clampInt(extrasForm.number_of_absences ?? 0, 0);
      setAbsText(String(n));
   }, [student?.id, extrasForm.number_of_absences]);

   const onAbsFocus = (e) => {
      absFocusedRef.current = true;
      const el = e.currentTarget;
      const n = clampInt(extrasForm.number_of_absences ?? 0, 0);
      setAbsText(n === 0 ? "" : String(n));
      requestAnimationFrame(() => {
         if (el && typeof el.select === "function") el.select();
      });
   };

   const onAbsChange = (e) => {
      const digits = String(e.target.value ?? "").replace(/[^\d]/g, "");
      const cleaned = digits.replace(/^0+(?=\d)/, "");
      setAbsText(cleaned);
      const n = cleaned === "" ? 0 : clampInt(parseInt(cleaned, 10), 0);
      setExtrasForm((p) => ({ ...p, number_of_absences: n }));
   };

   const commitAbsences = (n) => {
      const next = { ...extrasForm, number_of_absences: clampInt(n ?? 0, 0) };
      if (studentExtrasEqual(next, extrasBase)) return;
      requestSaveExtras(next);
   };

   const onAbsBlur = () => {
      absFocusedRef.current = false;
      const digits = String(absText ?? "").replace(/[^\d]/g, "");
      const cleaned = digits.replace(/^0+(?=\d)/, "");
      const n = cleaned === "" ? 0 : clampInt(parseInt(cleaned, 10), 0);
      setAbsText(String(n));
      setExtrasForm((p) => ({ ...p, number_of_absences: n }));
      commitAbsences(n);
   };

   const onAbsKeyDown = (e) => {
      if (e.key === "Enter") {
         e.preventDefault();
         e.currentTarget.blur();
      }
   };

   const saveSeqRef = useRef(Promise.resolve());
   const desiredRef = useRef(normStudentExtras(null));
   const lastSentRef = useRef("");

   useEffect(() => {
      saveSeqRef.current = Promise.resolve();
      desiredRef.current = normStudentExtras(student);
      lastSentRef.current = "";
   }, [student?.id]);

   const requestSaveExtras = (nextForm) => {
      if (!student?.id) return;
      const desired = normStudentExtras(nextForm);
      desiredRef.current = desired;
      setExtrasSaving(true);
      setExtrasError("");

      saveSeqRef.current = saveSeqRef.current
         .then(async () => {
            const cur = desiredRef.current;
            const curJson = JSON.stringify(cur);
            if (curJson === lastSentRef.current) return;

            const payload = buildStudentExtrasPayload(cur);
            const raw = await dispatch(
               updateStudent({ id: student.id, data: payload }),
            ).unwrap();
            const updated = unwrapUserResponse(raw);
            lastSentRef.current = curJson;

            const mismatches = [];
            if (
               typeof updated?.medical_documents === "boolean" &&
               updated.medical_documents !== cur.medical_documents
            )
               mismatches.push("medical_documents");
            if (
               typeof updated?.individual_work === "boolean" &&
               updated.individual_work !== cur.individual_work
            )
               mismatches.push("individual_work");
            if (
               updated?.number_of_absences != null &&
               Number(updated.number_of_absences) !== cur.number_of_absences
            )
               mismatches.push("number_of_absences");

            const serverState = normStudentExtras(updated ?? cur);

            if (mismatches.length) {
               setExtrasError(`Backend a ignorat: ${mismatches.join(", ")}.`);
               setExtrasBase(serverState);
               setExtrasForm(serverState);
            } else {
               setExtrasBase(serverState);
               const desiredNowJson = JSON.stringify(desiredRef.current);
               if (desiredNowJson === curJson) setExtrasForm(serverState);
            }

            setStudentFromGroup((prev) => {
               if (!prev || String(prev?.id) !== String(student?.id))
                  return prev;
               return { ...prev, ...(updated || cur) };
            });
         })
         .catch((e) => {
            const msg =
               e?.message ||
               e?.error ||
               (typeof e === "string" ? e : "");
            setExtrasError(msg ? String(msg) : "Salvarea a esuat.");
         })
         .finally(() => {
            const desiredNowJson = JSON.stringify(desiredRef.current);
            if (desiredNowJson === lastSentRef.current) setExtrasSaving(false);
         });
   };

   useEffect(() => {
      if (!student?.id || extrasSaving) return;
      const base = normStudentExtras(student);
      setExtrasBase(base);
      setExtrasForm(base);
      setExtrasError("");
   }, [
      student?.id,
      studentFromGroup?.medical_documents,
      studentFromGroup?.individual_work,
      studentFromGroup?.number_of_absences,
   ]);

   const setBooleanField = (key, value) => {
      setExtrasError("");
      setExtrasForm((p) => {
         const boolValue = Boolean(value);
         const next = { ...p, [key]: boolValue };
         if (Boolean(p[key]) !== boolValue) requestSaveExtras(next);
         return next;
      });
   };

   // load all data
   useEffect(() => {
      if (!studentId) {
         setLoading(false);
         setAllowed(false);
         setErr("Nu a fost selectat niciun student.");
         return;
      }
      let cancelled = false;

      (async () => {
         setLoading(true);
         setErr("");
         setAllowed(false);
         setCategoriesLoading(false);

         try {
            const sid = Number(studentId);

            const myStudentsRes = await getMyGroupStudents();
            const myList = Array.isArray(myStudentsRes?.students)
               ? myStudentsRes.students
               : [];
            const mySet = new Set(myList.map((s) => String(s?.id)));

            if (!mySet.has(String(studentId))) {
               throw new Error("Studentul nu este in grupa ta.");
            }

            const stFromGroup = myList.find(
               (s) => String(s?.id) === String(studentId),
            );

            const progRes = await getStudentPracticeProgress({
               studentId: sid,
               page: 1,
               limit: 200,
            });

            const stats = progRes?.statistics || null;
            const progressHistory = Array.isArray(progRes?.practiceHistory)
               ? progRes.practiceHistory
               : [];
            const hist = progressHistory;

            if (cancelled) return;

            setAllowed(true);
            setStudentFromGroup(stFromGroup || null);
            setStatistics(stats);
            setPracticeHistory(hist);
            setQuestionCategories([]);
         } catch (e) {
            if (cancelled) return;
            setErr(String(e?.message || e));
            setPracticeHistory([]);
            setQuestionCategories([]);
         } finally {
            if (cancelled) return;
            setLoading(false);
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [studentId]);

   useEffect(() => {
      if (!allowed) return;
      if (tab !== "categories") return;
      if (questionCategories.length > 0) return;

      let cancelled = false;
      (async () => {
         setCategoriesLoading(true);
         try {
            const catsRes = await getQuestionCategoriesWithCount();
            const categories = normalizePagedResponse(catsRes);
            if (!cancelled) setQuestionCategories(categories);
         } catch {
            if (!cancelled) setQuestionCategories([]);
         } finally {
            if (!cancelled) setCategoriesLoading(false);
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [allowed, tab, questionCategories.length]);

   const barPct = useMemo(() => {
      const sb = statistics?.statusBreakdown || null;
      if (!sb) return { ok: 0, bad: 0, skip: 100 };
      const okCount = Number(sb.PASSED || 0) + Number(sb.COMPLETED || 0);
      const badCount = Number(sb.FAILED || 0);
      const skipCount =
         Number(sb.IN_PROGRESS || 0) + Number(sb.NOT_STARTED || 0);
      const total = okCount + badCount + skipCount;
      if (total <= 0) return { ok: 0, bad: 0, skip: 100 };
      return {
         ok: (okCount / total) * 100,
         bad: (badCount / total) * 100,
         skip: (skipCount / total) * 100,
      };
   }, [statistics]);

   const DISPLAY_BASE = START_ID - 1;

   const ticketRows = useMemo(() => {
      if (TICKET_IDS.length > 0) {
         return TICKET_IDS.map((id) => ({
            id,
            nr: id - DISPLAY_BASE,
            totalQuestions: 0,
         }));
      }

      const ticketNrs = new Set();
      for (const it of practiceHistory) {
         const nr = parseTicketNr(it?.ticketName);
         if (nr) ticketNrs.add(nr);
      }

      return Array.from(ticketNrs)
         .sort((a, b) => a - b)
         .map((nr) => ({
            id: DISPLAY_BASE + nr,
            nr,
            totalQuestions: 0,
         }));
   }, [DISPLAY_BASE, practiceHistory]);

   const ticketIdToNrMap = useMemo(() => {
      const map = new Map();
      for (const row of ticketRows) {
         map.set(Number(row.id), Number(row.nr));
      }
      return map;
   }, [ticketRows]);

   const ticketAgg = useMemo(
      () => buildTicketAgg(practiceHistory, ticketIdToNrMap, DISPLAY_BASE),
      [practiceHistory, ticketIdToNrMap, DISPLAY_BASE],
   );

   const categoryAgg = useMemo(
      () => buildCategoryAgg(practiceHistory),
      [practiceHistory],
   );

   const allCategoryRows = useMemo(() => {
      const byId = new Map();

      for (const category of normalizePagedResponse(questionCategories)) {
         const id = safeInt(category?.id ?? category?.categoryId);
         if (id == null || id <= 0) continue;
         const title = catTitleRo(category);
         byId.set(id, {
            id,
            name: title,
            titleParts: buildCategoryTitleParts(title, "ro"),
            count: Number(getCatCount(category) || 0),
         });
      }

      return Array.from(byId.values()).sort((a, b) => a.id - b.id);
   }, [questionCategories]);

   const ticketVm = useMemo(() => {
      return ticketRows.map((row) => {
         const id = row.id;
         const nr = row.nr;
         const agg = ticketAgg[String(nr)] || null;
         const state = agg?.state || "none";
         const label = agg?.label || "Neînceput";
         const scoreText =
            agg &&
            Number.isFinite(Number(agg.correct)) &&
            Number.isFinite(Number(agg.total)) &&
            Number(agg.total) > 0
               ? `${Math.min(Number(agg.correct), Number(agg.total))}/${Number(agg.total)}`
               : "";
         return {
            id,
            nr,
            state,
            label,
            scoreText,
            totalQuestions: Number(row.totalQuestions || 0),
         };
      });
   }, [ticketAgg, ticketRows]);

   const visibleTickets = useMemo(() => {
      const qq = q.trim().toLowerCase();
      if (!qq) return ticketVm;
      return ticketVm.filter(
         (t) => String(t.nr).includes(qq) || String(t.id).includes(qq),
      );
   }, [ticketVm, q]);

   const visibleCategories = useMemo(() => {
      const qq = q.trim().toLowerCase();
      if (!qq) return allCategoryRows;
      return allCategoryRows.filter((c) =>
         `${c.name} ${c.id}`.toLowerCase().includes(qq),
      );
   }, [allCategoryRows, q]);

   const catStatById = useMemo(() => {
      const out = {};
      for (const c of allCategoryRows) {
         const agg = categoryAgg[String(c.id)] || null;
         const state = agg?.state || "none";
         const st = agg?.st || STATUS.NOT_STARTED;
         const totalDisp =
            Number.isFinite(Number(agg?.total)) && Number(agg.total) >= 0
               ? Number(agg.total)
               : Number(c.count) || 0;
         const correctDisp =
            Number.isFinite(Number(agg?.correct)) && Number(agg.correct) >= 0
               ? Number(agg.correct)
               : null;
         out[String(c.id)] = {
            state,
            st,
            total: totalDisp,
            correct: correctDisp,
         };
      }
      return out;
   }, [allCategoryRows, categoryAgg]);

   const latestPracticeEntries = useMemo(
      () =>
         buildLatestPracticeEntries(
            practiceHistory,
            ticketIdToNrMap,
            DISPLAY_BASE,
         ),
      [practiceHistory, ticketIdToNrMap, DISPLAY_BASE],
   );

   const summaryLine = useMemo(() => {
      const passedTickets = ticketVm.filter(
         (ticket) => ticket.state === "ok",
      ).length;
      const inProgressTickets = ticketVm.filter(
         (ticket) => ticket.state === "progress",
      ).length;

      const avgScore = Number(statistics?.averageScore);
      const scoreText = Number.isFinite(avgScore)
         ? `${avgScore.toFixed(2)}%`
         : "0%";

      return `Trecute ${passedTickets}, în proces ${inProgressTickets} score ${scoreText}`;
   }, [ticketVm, statistics]);

   const studentPracticeTotals = useMemo(() => {
      let totalQuestions = 0,
         correct = 0,
         wrong = 0,
         unanswered = 0;
      for (const it of latestPracticeEntries) {
         const totalQ = Math.max(0, safeInt(it?.totalQuestions) ?? 0);
         totalQuestions += totalQ;

         if (totalQ <= 0) continue;

         const st = normalizeStatus(it?.status);
         const score = safeInt(it?.score);

         if (score != null) {
            const c = Math.max(0, Math.min(totalQ, score));
            correct += c;
            if (st === STATUS.IN_PROGRESS) {
               const lastQuestionIndex = safeInt(it?.lastQuestionIndex) ?? c;
               const answered = Math.max(
                  c,
                  Math.max(0, Math.min(totalQ, lastQuestionIndex)),
               );
               wrong += Math.max(0, answered - c);
               unanswered += Math.max(0, totalQ - answered);
            } else {
               wrong += Math.max(0, totalQ - c);
            }
            continue;
         }

         if (st === STATUS.IN_PROGRESS) {
            const lastQuestionIndex = safeInt(it?.lastQuestionIndex) ?? 0;
            const answered = Math.max(0, Math.min(totalQ, lastQuestionIndex));
            unanswered += Math.max(0, totalQ - answered);
         } else {
            unanswered += totalQ;
         }
      }
      return { totalQuestions, correct, wrong, unanswered };
   }, [latestPracticeEntries]);

   const profilePracticeHistory = useMemo(() => {
      return [...(Array.isArray(practiceHistory) ? practiceHistory : [])]
         .sort((a, b) => tsOf(b) - tsOf(a))
         .map((item, index) => {
            const st = normalizeStatus(item?.status);
            const state = statusToState(st);
            const total = Math.max(0, safeInt(item?.totalQuestions) ?? 0);
            const scoreRaw = safeInt(item?.score);
            const score =
               scoreRaw == null ? null : Math.max(0, Math.min(total, scoreRaw));
            const name = String(item?.ticketName || "").trim();

            return {
               id: safeInt(item?.id) ?? `row-${index}`,
               name: name || `Practică #${safeInt(item?.id) ?? index + 1}`,
               state,
               statusLabel: statusLabelRo(st),
               scoreText:
                  score != null && total > 0
                     ? `${score}/${total}`
                     : total > 0
                       ? `0/${total}`
                       : "",
               dateText: formatHistoryDate(
                  item?.completedAt ||
                     item?.finishedAt ||
                     item?.endedAt ||
                     item?.startedAt ||
                     item?.createdAt,
               ),
            };
         });
   }, [practiceHistory]);

   const studentIdnp = resolveStudentIdnp(student);
   const studentFirstName =
      String(student?.firstName || "").trim() || "Lipsește";
   const studentLastName = String(student?.lastName || "").trim() || "Lipsește";
   const studentPhone = String(student?.phone || "").trim() || "Lipsește";
   const studentEmail = String(student?.email || "").trim() || "Lipsește";
   const studentIdnpDisplay = studentIdnp || "Lipsește";

   return (
      <div className="instrStudentsPopup">
         <div className="instrStudentsPopup__header">
            <h3 className="instrStudentsPopup__title">Statistica elev</h3>
         </div>

         <div className="instrStudentsPopup__content">
            <StudentProfileItem student={student} />

            {!loading && err && (
               <div className="instrStudentsPopup__state instrStudentsPopup__state--error">
                  {err}
               </div>
            )}
            {!loading && !err && !allowed && (
               <div className="instrStudentsPopup__state instrStudentsPopup__state--error">
                  Acces interzis.
               </div>
            )}

            <div className="instrStudentsPopup__tabs">
               <button
                  type="button"
                  className={
                     "instrStudentsPopup__tab" +
                     (tab === "profile" ? " is-active" : "")
                  }
                  onClick={() => setTab("profile")}
               >
                  Profil
               </button>
               <button
                  type="button"
                  className={
                     "instrStudentsPopup__tab" +
                     (tab === "themes" ? " is-active" : "")
                  }
                  onClick={() => setTab("themes")}
               >
                  Teme
               </button>
               <button
                  type="button"
                  className={
                     "instrStudentsPopup__tab" +
                     (tab === "categories" ? " is-active" : "")
                  }
                  onClick={() => setTab("categories")}
               >
                  Categorii
               </button>
            </div>

            {!err && (allowed || loading) && (
               <div className="instrStudentsPopup__panel">
                  {tab === "profile" ? (
                     <div className="instrStudentsPopup__form">
                        <div className="instrStudentsPopup__fieldGrid">
                           <label className="instrStudentsPopup__field">
                              <span className="instrStudentsPopup__fieldLabel">
                                 Total întrebări
                              </span>
                              <input
                                 type="text"
                                 value={studentPracticeTotals.totalQuestions}
                                 readOnly
                                 className="instrStudentsPopup__input"
                              />
                           </label>

                           <label className="instrStudentsPopup__field">
                              <span className="instrStudentsPopup__fieldLabel">
                                 Răspunsuri corecte
                              </span>
                              <input
                                 type="text"
                                 value={studentPracticeTotals.correct}
                                 readOnly
                                 className="instrStudentsPopup__input"
                              />
                           </label>

                           <label className="instrStudentsPopup__field">
                              <span className="instrStudentsPopup__fieldLabel">
                                 Răspunsuri greșite
                              </span>
                              <input
                                 type="text"
                                 value={studentPracticeTotals.wrong}
                                 readOnly
                                 className="instrStudentsPopup__input"
                              />
                           </label>

                           <label className="instrStudentsPopup__field">
                              <span className="instrStudentsPopup__fieldLabel">
                                 Întrebări necompletate
                              </span>
                              <input
                                 type="text"
                                 value={studentPracticeTotals.unanswered}
                                 readOnly
                                 className="instrStudentsPopup__input"
                              />
                           </label>

                           <div className="instrStudentsPopup__field instrStudentsPopup__field--full instrStudentsPopup__progressField">
                              <span className="instrStudentsPopup__fieldLabel">
                                 Progres
                              </span>
                              {loading ? (
                                 <p className="instrStudentsPopup__progressText">
                                    Se incarca statistica...
                                 </p>
                              ) : (
                                 <>
                                    <p className="instrStudentsPopup__progressText">
                                       {summaryLine}
                                    </p>
                                    <div className="instrStudentsPopup__progressWrap">
                                       <SegmentedBar
                                          pctCorrect={barPct.ok}
                                          pctWrong={barPct.bad}
                                          pctUnanswered={barPct.skip}
                                          basePx={12}
                                       />
                                    </div>
                                 </>
                              )}
                           </div>

                           <label className="instrStudentsPopup__field">
                              <span className="instrStudentsPopup__fieldLabel">
                                 Nume
                              </span>
                              <input
                                 type="text"
                                 value={studentLastName}
                                 readOnly
                                 className="instrStudentsPopup__input"
                              />
                           </label>

                           <label className="instrStudentsPopup__field">
                              <span className="instrStudentsPopup__fieldLabel">
                                 Prenume
                              </span>
                              <input
                                 type="text"
                                 value={studentFirstName}
                                 readOnly
                                 className="instrStudentsPopup__input"
                              />
                           </label>

                           <label className="instrStudentsPopup__field">
                              <span className="instrStudentsPopup__fieldLabel">
                                 Telefon
                              </span>
                              <input
                                 type="text"
                                 value={studentPhone}
                                 readOnly
                                 className="instrStudentsPopup__input"
                              />
                           </label>

                           <label className="instrStudentsPopup__field">
                              <span className="instrStudentsPopup__fieldLabel">
                                 Email
                              </span>
                              <input
                                 type="text"
                                 value={studentEmail}
                                 readOnly
                                 className="instrStudentsPopup__input"
                              />
                           </label>

                           <label className="instrStudentsPopup__field">
                              <span className="instrStudentsPopup__fieldLabel">
                                 IDNP
                              </span>
                              <input
                                 type="text"
                                 value={studentIdnpDisplay}
                                 readOnly
                                 className="instrStudentsPopup__input"
                              />
                           </label>

                           <label className="instrStudentsPopup__field">
                              <span className="instrStudentsPopup__fieldLabel">
                                 Absențe
                              </span>
                              <input
                                 type="text"
                                 name="number_of_absences"
                                 inputMode="numeric"
                                 pattern="[0-9]*"
                                 value={absText}
                                 disabled={extrasSaving || loading}
                                 onFocus={onAbsFocus}
                                 onChange={onAbsChange}
                                 onBlur={onAbsBlur}
                                 onKeyDown={onAbsKeyDown}
                                 className="instrStudentsPopup__input"
                              />
                           </label>

                           <label className="instrStudentsPopup__field instrStudentsPopup__field--toggle instrStudentsPopup__field--full">
                              <span className="instrStudentsPopup__fieldLabel">
                                 Doc. medicale
                              </span>
                              <UIToggle
                                 checked={extrasForm.medical_documents === true}
                                 onChange={(checked) =>
                                    setBooleanField(
                                       "medical_documents",
                                       checked,
                                    )
                                 }
                                 disabled={extrasSaving || loading}
                                 aria-label="Doc. medicale"
                              />
                           </label>

                           <label className="instrStudentsPopup__field instrStudentsPopup__field--toggle instrStudentsPopup__field--full">
                              <span className="instrStudentsPopup__fieldLabel">
                                 Lucru individual
                              </span>
                              <UIToggle
                                 checked={extrasForm.individual_work === true}
                                 onChange={(checked) =>
                                    setBooleanField("individual_work", checked)
                                 }
                                 disabled={extrasSaving || loading}
                                 aria-label="Lucru individual"
                              />
                           </label>
                        </div>

                        {extrasSaving && (
                           <p className="instrStudentsPopup__autosave">
                              Se salvează automat...
                           </p>
                        )}

                        {extrasError && (
                           <div className="instrStudentsPopup__state instrStudentsPopup__state--error instrStudentsPopup__inlineError">
                              {extrasError}
                           </div>
                        )}

                        <section className="instrStudentsPopup__history">
                           <header className="instrStudentsPopup__historyHead">
                              Istoric practică
                           </header>

                           {loading ? (
                              <div className="instrStudentsPopup__historyEmpty">
                                 Se încarcă istoricul...
                              </div>
                           ) : profilePracticeHistory.length === 0 ? (
                              <div className="instrStudentsPopup__historyEmpty">
                                 Nu există încercări de practică.
                              </div>
                           ) : (
                              <div className="instrStudentsPopup__historyList">
                                 {profilePracticeHistory.map((row) => (
                                    <article
                                       key={row.id}
                                       className={
                                          "instrStudentsPopup__historyRow" +
                                          (row.state === "ok"
                                             ? " instrStudentsPopup__historyRow--ok"
                                             : row.state === "bad"
                                               ? " instrStudentsPopup__historyRow--bad"
                                               : row.state === "progress"
                                                 ? " instrStudentsPopup__historyRow--progress"
                                                 : "")
                                       }
                                    >
                                       <div className="instrStudentsPopup__historyMain">
                                          <div className="instrStudentsPopup__historyTitle">
                                             {row.name}
                                          </div>
                                          <div className="instrStudentsPopup__historyDate">
                                             {row.dateText}
                                          </div>
                                       </div>

                                       <div className="instrStudentsPopup__historyRight">
                                          <span className="instrStudentsPopup__historyStatus">
                                             {row.statusLabel}
                                          </span>
                                          {row.scoreText ? (
                                             <span className="instrStudentsPopup__historyScore">
                                                {row.scoreText}
                                             </span>
                                          ) : null}
                                       </div>
                                    </article>
                                 ))}
                              </div>
                           )}
                        </section>
                     </div>
                  ) : tab === "themes" ? (
                     <div className="instrStudentsPopup__ticketGrid">
                        {visibleTickets.map((t) => {
                           const ticketClass =
                              "instrStudentsPopup__ticket" +
                              (t.state === "ok"
                                 ? " instrStudentsPopup__ticket--ok"
                                 : t.state === "bad"
                                   ? " instrStudentsPopup__ticket--bad"
                                   : t.state === "progress"
                                     ? " instrStudentsPopup__ticket--progress"
                                     : "");
                           const metaText =
                              t.scoreText ||
                              (t.state === "progress" ? "În lucru" : "");
                           return (
                              <article
                                 key={t.id}
                                 className={ticketClass}
                                 title={`ID: ${t.id} • ${t.label}`}
                              >
                                 <div className="instrStudentsPopup__ticketTitle">
                                    Bilet {t.nr}
                                 </div>
                                 {metaText ? (
                                    <div className="instrStudentsPopup__ticketMeta">
                                       <span className="instrStudentsPopup__ticketScore">
                                          {metaText}
                                       </span>
                                    </div>
                                 ) : null}
                              </article>
                           );
                        })}
                     </div>
                  ) : (
                     <div className="instrStudentsPopup__catList">
                        {visibleCategories.length === 0
                           ? !categoriesLoading && (
                                <div className="instrStudentsPopup__empty">
                                   Nu exista categorii.
                                </div>
                             )
                           : visibleCategories.map((c) => {
                                const stat = catStatById[String(c.id)] || {
                                   state: "none",
                                   total: Number(c.count) || 0,
                                   correct: null,
                                   st: STATUS.NOT_STARTED,
                                };
                                const itemClass =
                                   "instrStudentsPopup__catItem" +
                                   (stat.state === "ok"
                                      ? " instrStudentsPopup__catItem--ok"
                                      : "") +
                                   (stat.state === "bad"
                                      ? " instrStudentsPopup__catItem--bad"
                                      : "") +
                                   (stat.state === "progress"
                                      ? " instrStudentsPopup__catItem--progress"
                                      : "");

                                const badgeClass =
                                   "instrStudentsPopup__catBadge" +
                                   (stat.state === "ok"
                                      ? " instrStudentsPopup__catBadge--ok"
                                      : "") +
                                   (stat.state === "bad"
                                      ? " instrStudentsPopup__catBadge--bad"
                                      : "") +
                                   (stat.state === "progress"
                                      ? " instrStudentsPopup__catBadge--progress"
                                      : "") +
                                   (stat.state === "none"
                                      ? " instrStudentsPopup__catBadge--none"
                                      : "");
                                const totalDisp =
                                   Number(stat.total ?? c.count ?? 0) || 0;
                                const correctDisp =
                                   Number.isFinite(Number(stat.correct)) &&
                                   Number(stat.correct) >= 0
                                      ? Number(stat.correct)
                                      : null;
                                const badgeText =
                                   (stat.st === STATUS.PASSED ||
                                      stat.st === STATUS.COMPLETED ||
                                      stat.st === STATUS.FAILED ||
                                      stat.st === STATUS.IN_PROGRESS) &&
                                   correctDisp != null &&
                                   totalDisp > 0
                                      ? `${Math.min(correctDisp, totalDisp)}/${totalDisp}`
                                      : stat.state === "ok" && totalDisp > 0
                                        ? `${totalDisp}/${totalDisp}`
                                        : statusLabelRo(stat.st);
                                return (
                                   <article
                                      key={c.id}
                                      className={itemClass}
                                      title={`${c.name} • ID: ${c.id}`}
                                   >
                                      <div className="instrStudentsPopup__catLeft">
                                         <div className="instrStudentsPopup__catTitle">
                                            {(c.titleParts?.length
                                               ? c.titleParts
                                               : [
                                                    {
                                                       marker: null,
                                                       text: c.name,
                                                    },
                                                 ]
                                            ).map((part, index) => (
                                               <span
                                                  key={`${c.id}-${index}`}
                                                  className="instrStudentsPopup__catLine"
                                               >
                                                  {part.marker ? (
                                                     <span className="instrStudentsPopup__catLineMark">
                                                        {part.marker}
                                                     </span>
                                                  ) : null}
                                                  {part.text ? (
                                                     <span className="instrStudentsPopup__catLineText">
                                                        {part.text}
                                                     </span>
                                                  ) : null}
                                               </span>
                                            ))}
                                         </div>
                                      </div>
                                      <div className={badgeClass}>
                                         {badgeText}
                                      </div>
                                   </article>
                                );
                             })}
                     </div>
                  )}
               </div>
            )}
         </div>
      </div>
   );
}
